import React, { useState } from 'react';
import {
  styled,
  IconButton,
  Box,
  Menu,
  MenuItem,
  SwipeableDrawer as MuiSwipeableDrawer,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Icons, OutlinedIcons, PrimaryAppBar,
} from '@marageti/z4-lib';
import { Link as RouterLink } from 'react-router-dom';
import { useJustCallDialer } from '../../hooks/use-justcall-dialer';
import { FEATURES, FeatureFlag } from '../../store/contexts/feature-flags';
import { ReactComponent as OryxIcon } from '../../assets/svg/Oryx.svg';
import { ReactComponent as ActiveJustCallIcon } from '../../assets/svg/justcall-active.svg';
import { ReactComponent as InactiveJustCallIcon } from '../../assets/svg/justcall-inactive.svg';
import { ReactComponent as OfflineJustCallIcon } from '../../assets/svg/justcall-offline.svg';
import {
  MainNavListDesktop, MainNavListMobile, UserMenuDesktop, UserNavListMobile,
} from './menus-view';

const Drawer = styled(MuiSwipeableDrawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    background: theme.palette.primary.main,
    color: theme.palette.background.default,
    boxSizing: 'border-box',
    width: '100%',
  },
}));

const phoneDialerMenuId = 'phone-dialer-menu';

const NavigationView = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [desktopUserMenuAnchor, setDesktopUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [phoneMenuAnchor, setPhoneMenuAnchor] = useState<null | HTMLElement>(null);
  const userMenuOpen = !!desktopUserMenuAnchor;
  const phoneMenuOpen = Boolean(phoneMenuAnchor);
  const {
    isDialerMounted,
    isDialerLockBlocked,
    isDialerLockHeldRemotely,
    retryJustCallDialerLock,
    signInToDialer,
    openDialer,
    signOutFromDialer,
  } = useJustCallDialer();

  type JustCallNavIcon = React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  let PhoneDialerIcon: JustCallNavIcon = OfflineJustCallIcon;
  if (isDialerMounted) {
    PhoneDialerIcon = ActiveJustCallIcon;
  } else if (isDialerLockHeldRemotely) {
    PhoneDialerIcon = InactiveJustCallIcon;
  }

  let phoneDialerAriaLabel = 'Phone and JustCall dialer';
  let phoneDialerTooltipTitle = 'Offline · Sign in to receive calls';
  if (isDialerMounted) {
    phoneDialerAriaLabel = 'Phone — JustCall dialer active in this tab';
    phoneDialerTooltipTitle = 'Available for calls';
  } else if (isDialerLockHeldRemotely) {
    phoneDialerAriaLabel = 'Phone — dialer active in another tab or window';
    phoneDialerTooltipTitle = 'Unavailable for calls';
  }

  const handleClickAvatar = (e: React.MouseEvent<HTMLElement>) => {
    setDesktopUserMenuAnchor(e.currentTarget);
  };

  const handleCloseUserMenuItems = () => {
    setDesktopUserMenuAnchor(null);
  };

  const handleClose = () => {
    setDrawerOpen(false);
  };
  const handleOpen = () => {
    setDrawerOpen(true);
  };

  const handlePhoneMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPhoneMenuAnchor(event.currentTarget);
  };

  const handlePhoneMenuClose = () => {
    setPhoneMenuAnchor(null);
  };

  const phoneDialerMenuItems = (() => {
    if (isDialerLockBlocked) {
      return (
        <>
          <MenuItem disabled>
            Phone active in another tab or window
          </MenuItem>
          <MenuItem
            onClick={() => {
              retryJustCallDialerLock();
              handlePhoneMenuClose();
            }}
          >
            Try again
          </MenuItem>
          <MenuItem
            onClick={() => {
              signOutFromDialer();
              handlePhoneMenuClose();
            }}
          >
            Cancel
          </MenuItem>
        </>
      );
    }
    if (!isDialerMounted) {
      if (isDialerLockHeldRemotely) {
        return (
          <MenuItem disabled>
            Dialer is open in another tab
          </MenuItem>
        );
      }
      return (
        <MenuItem
          onClick={() => {
            signInToDialer();
            handlePhoneMenuClose();
          }}
        >
          Sign in to JustCall
        </MenuItem>
      );
    }
    return (
      <>
        <MenuItem
          onClick={() => {
            openDialer();
            handlePhoneMenuClose();
          }}
        >
          Open Dialer
        </MenuItem>
        <MenuItem
          onClick={() => {
            signOutFromDialer();
            handlePhoneMenuClose();
          }}
        >
          Sign off
        </MenuItem>
      </>
    );
  })();

  return (
    <>
      <PrimaryAppBar
        position="sticky"
        variant="center"
        color="primary"
        startAdornment={(
          <>
            {/* Desktop */}
            <IconButton
              component={RouterLink}
              to="/"
              aria-label="Home page"
              color="inherit"
              edge="start"
              sx={{
                display: {
                  desktop: 'flex',
                  mobile: 'none',
                },
              }}
            >
              <OryxIcon />
            </IconButton>

            {/* Mobile */}
            <IconButton
              aria-label="menu"
              color="inherit"
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{
                display: {
                  desktop: 'none',
                  mobile: 'flex',
                },
              }}
            >
              <OutlinedIcons.OutlinedMenuAlt3Icon />
            </IconButton>
          </>
        )}
        endAdornment={(
          <Stack direction="row">
            <FeatureFlag feature={FEATURES.JUST_CALL_DIALER}>
              <>
                <Tooltip title={phoneDialerTooltipTitle} placement="bottom">
                  <IconButton
                    type="button"
                    color="inherit"
                    aria-label={phoneDialerAriaLabel}
                    aria-controls={phoneMenuOpen ? phoneDialerMenuId : undefined}
                    aria-expanded={phoneMenuOpen ? 'true' : 'false'}
                    aria-haspopup="true"
                    onClick={handlePhoneMenuOpen}
                    sx={{
                      '& svg': {
                        display: 'block',
                        width: 24,
                        height: 24,
                      },
                    }}
                  >
                    <PhoneDialerIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  id={phoneDialerMenuId}
                  anchorEl={phoneMenuAnchor}
                  open={phoneMenuOpen}
                  onClose={handlePhoneMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {phoneDialerMenuItems}
                </Menu>
              </>
            </FeatureFlag>
            {/* Desktop */}
            <IconButton
              component={RouterLink}
              to="/settings"
              color="inherit"
              aria-label="settings"
              sx={{
                display: {
                  desktop: 'flex',
                  mobile: 'none',
                },
              }}
            >
              <OutlinedIcons.OutlinedCogIcon />
            </IconButton>
            <IconButton
              edge="end"
              color="inherit"
              aria-controls="user-menu"
              aria-expanded={userMenuOpen ? 'true' : 'false'}
              aria-haspopup="true"
              aria-label="user menu"
              onClick={handleClickAvatar}
              sx={{
                display: {
                  desktop: 'flex',
                  mobile: 'none',
                },
              }}
            >
              <OutlinedIcons.OutlinedUserIcon />
            </IconButton>
          </Stack>
        )}
      >
        {/* Desktop */}
        <MainNavListDesktop sx={{ display: { mobile: 'none', desktop: 'flex' } }} />

        {/* Mobile */}
        <IconButton
          aria-label="Home page"
          color="inherit"
          component={RouterLink}
          to="/"
          sx={{
            display: {
              desktop: 'none',
              mobile: 'flex',
            },
          }}
        >
          <OryxIcon />
        </IconButton>
      </PrimaryAppBar>
      <UserMenuDesktop
        anchorEl={desktopUserMenuAnchor}
        open={userMenuOpen}
        handleClose={handleCloseUserMenuItems}
      />
      <Drawer
        anchor="left"
        hideBackdrop
        onClose={handleClose}
        onOpen={handleOpen}
        open={drawerOpen}
        variant="temporary"
      >
        <Box
          onClick={handleClose}
          sx={{
            display: 'flex',
            p: 5,
            justifyContent:
              'space-between',
            alignItems: 'flex-start',
            height: '100%',
          }}
        >
          <Box
            component="nav"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
              flex: 1,
            }}
          >
            <MainNavListMobile />
            <UserNavListMobile />
          </Box>
          <IconButton
            color="inherit"
          >
            <Icons.X />
          </IconButton>
        </Box>
      </Drawer>
    </>
  );
};

export default NavigationView;
